import { makeAutoObservable, runInAction } from 'mobx';
import { makePersistable } from 'mobx-persist-store';
import agent from '../api/agent';
import {
  GatewayCategoryPageRequestDto,
  GatewayCategoryPageResponseDto,
} from '../models/gateway-category';
import { handleError } from '../utils/generic/generic.utils';
import i18n from '../../i18n';

export type CategoryPageUpdateMode = 'initialize' | 'forward' | 'replace';

interface CategoryNavigationEntry {
  request: GatewayCategoryPageRequestDto;
  title?: string;
}

export default class CategoryStore {
  readonly hydrationPromises: Promise<any>[] = [];

  categoriesPage: GatewayCategoryPageResponseDto | null = null;

  currentNavigationPage: GatewayCategoryPageResponseDto | null = null;

  navigationEntries: CategoryNavigationEntry[] = [];

  filterText: string = '';

  isLoadingCategoriesPage: boolean = false;

  private loadingRequestsCount: number = 0;

  constructor() {
    makeAutoObservable(this);
    this.hydrationPromises.push(
      makePersistable(this, {
        name: 'CategoryStoreSession',
        version: 1,
        properties: [
          'categoriesPage',
          'currentNavigationPage',
          'navigationEntries',
          'filterText',
        ],
        storage: window.sessionStorage,
      })
    );
  }

  get canNavigateBack() {
    return this.navigationEntries.length > 1;
  }

  get currentParentId() {
    return this.navigationEntries.at(-1)?.request.filter.parentId ?? null;
  }

  get currentPageTitle() {
    return this.filterText
      ? undefined
      : this.navigationEntries.at(-1)?.title ??
          i18n.t('delivery_options_label_popular');
  }

  private hasSameNavigationContext = (
    request: GatewayCategoryPageRequestDto
  ) => {
    const currentRequest = this.navigationEntries.at(-1)?.request;

    return (
      currentRequest?.destination === request.destination &&
      currentRequest.shippingMethod === request.shippingMethod &&
      currentRequest.filter.warehouseId === request.filter.warehouseId
    );
  };

  requestCategoriesPage = async (
    request: GatewayCategoryPageRequestDto,
    trackLoading: boolean = true
  ) => {
    if (trackLoading) {
      this.loadingRequestsCount += 1;
      this.isLoadingCategoriesPage = true;
    }

    try {
      return await agent.Gateway.Category.getCategoriesPage(request);
    } catch (error) {
      handleError(error);
      return undefined;
    } finally {
      if (trackLoading) {
        runInAction(() => {
          this.loadingRequestsCount = Math.max(
            0,
            this.loadingRequestsCount - 1
          );
          this.isLoadingCategoriesPage = this.loadingRequestsCount > 0;
        });
      }
    }
  };

  getCategoriesPage = async (
    request: GatewayCategoryPageRequestDto,
    updateMode: CategoryPageUpdateMode = 'replace',
    pageTitle?: string
  ) => {
    if (
      updateMode === 'initialize' &&
      this.currentNavigationPage &&
      this.hasSameNavigationContext(request)
    ) {
      if (!this.categoriesPage) {
        runInAction(() => {
          this.categoriesPage = this.currentNavigationPage;
          this.filterText = '';
        });
      }

      return this.categoriesPage!;
    }

    const categoriesPage = await this.requestCategoriesPage(request);

    if (!categoriesPage) return undefined;

    runInAction(() => {
      this.categoriesPage = categoriesPage;
      this.filterText = request.filter.text;

      if (updateMode === 'initialize') {
        this.navigationEntries = [{ request, title: pageTitle }];
        this.currentNavigationPage = categoriesPage;
      }

      if (updateMode === 'forward') {
        this.navigationEntries.push({ request, title: pageTitle });
        this.currentNavigationPage = categoriesPage;
      }
    });

    return categoriesPage;
  };

  navigateBack = async () => {
    if (!this.canNavigateBack) return this.currentNavigationPage ?? undefined;

    const previousRequest = this.navigationEntries.at(-2)!.request;
    const categoriesPage = await this.requestCategoriesPage(previousRequest);

    if (!categoriesPage) return undefined;

    runInAction(() => {
      this.navigationEntries.pop();
      this.currentNavigationPage = categoriesPage;
      this.categoriesPage = categoriesPage;
      this.filterText = '';
    });

    return categoriesPage;
  };

  restoreCurrentNavigationPage = () => {
    this.categoriesPage = this.currentNavigationPage;
    this.filterText = '';
  };

  setFilterText = (filterText: string) => {
    this.filterText = filterText;
  };

  reset = () => {
    this.categoriesPage = null;
    this.currentNavigationPage = null;
    this.navigationEntries = [];
    this.filterText = '';
    this.isLoadingCategoriesPage = false;
    this.loadingRequestsCount = 0;
  };
}
